import type { SurveyDocument } from '@apruv/survey-contract/types';

/** Wire contract for the survey preview bridge; both consumers must use identical definitions. */
export const PREVIEW_PROTOCOL = 1;
export const CANONICAL_PREVIEW_PROTOCOL = 2;
export const PREVIEW_CHANNEL = 'apruv:survey-preview';
export const MAX_PREVIEW_BYTES = 1_000_000;
export type PreviewMessage = {
  channel: typeof PREVIEW_CHANNEL;
  version: typeof PREVIEW_PROTOCOL | typeof CANONICAL_PREVIEW_PROTOCOL;
  session: string;
  instance: string;
  revision: number;
  type: 'probe' | 'ready' | 'configure' | 'ack' | 'action' | 'complete' | 'error';
  payload?: Record<string, unknown>;
};

export type CanonicalPreviewConfiguration = {
  kind: 'canonical';
  authoring?: {
    mode: 'select';
    selectedComponentId?: string;
  };
  document: SurveyDocument;
  scenario: {
    locale: string;
    scoreMethod: 'nps' | 'csat';
    serviceMedia: Array<{
      type: 'image' | 'video' | 'voice';
      url?: string;
      thumbnailUrl?: string;
      label?: string;
      notes?: string;
    }>;
    technicianNotes: string;
    technicianFirstName?: string;
    companyName: string;
    locationName: string;
    tippingEligible: boolean;
    serviceLifecycle?: 'initial_service' | 'regular_service';
    existingResponse?: {
      comments?: string | null;
      rating: number;
      ratingId: number;
      scoreMethod: 'NPS' | 'CSAT';
      submitted: true;
      tags?: string[] | null;
    };
  };
  initialScreenId?: string;
  initialValues?: Record<string, unknown>;
};

const record = (value: unknown): value is Record<string, unknown> =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  [Object.prototype, null].includes(Object.getPrototypeOf(value));

/** Structured-clone accepts more than JSON; reject cycles, prototypes and unbounded payloads. */
export function boundedPreviewJSON(value: unknown, omitUndefined = false): boolean {
  let nodes = 0;
  const seen = new Set<object>();
  const visit = (item: unknown, depth: number): boolean => {
    if (++nodes > 50_000 || depth > 32) return false;
    if (item === undefined) return omitUndefined;
    if (item === null || typeof item === 'boolean') return true;
    if (typeof item === 'number') return Number.isFinite(item);
    if (typeof item === 'string') return item.length <= 100_000;
    if (!item || typeof item !== 'object' || seen.has(item)) return false;
    if (!Array.isArray(item) && !record(item)) return false;
    seen.add(item);
    const entries = Object.getOwnPropertyDescriptors(item);
    if (Reflect.ownKeys(entries).some((key) => typeof key !== 'string')) return false;
    const valid = Object.entries(entries).every(
      ([key, descriptor]) =>
        key.length <= 256 && 'value' in descriptor && visit(descriptor.value, depth + 1)
    );
    seen.delete(item);
    return valid;
  };
  try {
    return (
      visit(value, 0) && new TextEncoder().encode(JSON.stringify(value)).length <= MAX_PREVIEW_BYTES
    );
  } catch {
    return false;
  }
}

export function exactPreviewOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

export function previewMessage(value: unknown): PreviewMessage | null {
  if (!record(value) || !boundedPreviewJSON(value)) return null;
  if (
    Object.keys(value).some(
      (key) =>
        !['channel', 'version', 'session', 'instance', 'revision', 'type', 'payload'].includes(key)
    )
  )
    return null;
  if (
    value.channel !== PREVIEW_CHANNEL ||
    ![PREVIEW_PROTOCOL, CANONICAL_PREVIEW_PROTOCOL].includes(value.version as number) ||
    typeof value.instance !== 'string' ||
    !/^[a-zA-Z0-9-]{16,80}$/.test(value.instance) ||
    typeof value.session !== 'string' ||
    !/^[a-zA-Z0-9-]{16,80}$/.test(value.session) ||
    !Number.isSafeInteger(value.revision) ||
    (value.revision as number) < 0 ||
    (!['ready', 'probe'].includes(value.type as string) && value.revision === 0) ||
    !['probe', 'ready', 'configure', 'ack', 'action', 'complete', 'error'].includes(
      value.type as string
    )
  )
    return null;
  if (['probe', 'ready', 'ack'].includes(value.type as string)) {
    if (
      value.payload !== undefined ||
      (['ready', 'probe'].includes(value.type as string) && value.revision !== 0)
    )
      return null;
  } else if (!record(value.payload)) return null;
  const payload = value.payload as Record<string, unknown> | undefined;
  if (value.type === 'configure') {
    const valid =
      value.version === CANONICAL_PREVIEW_PROTOCOL
        ? validCanonicalPreviewConfiguration(value.payload)
        : validPreviewConfiguration(value.payload);
    if (!valid) return null;
  }
  if (
    value.type === 'action' &&
    (!record(payload!.action) || typeof payload!.action.type !== 'string')
  )
    return null;
  if (
    value.type === 'complete' &&
    (!record(payload!.values) ||
      (payload!.completionId !== undefined &&
        (!Number.isSafeInteger(payload!.completionId) || (payload!.completionId as number) < 1)))
  )
    return null;
  if (
    value.type === 'error' &&
    !['invalid_configuration', 'incompatible_version'].includes(payload!.reason as string)
  )
    return null;
  return value as PreviewMessage;
}

/** Version two carries an authoring document; version one remains the legacy flow wire. */
export function validCanonicalPreviewConfiguration(
  value: unknown
): value is CanonicalPreviewConfiguration {
  if (
    !record(value) ||
    value.kind !== 'canonical' ||
    !record(value.document) ||
    !record(value.scenario)
  )
    return false;
  if (
    Object.keys(value).some(
      (key) =>
        !['kind', 'authoring', 'document', 'scenario', 'initialScreenId', 'initialValues'].includes(
          key
        )
    )
  )
    return false;
  if (
    value.authoring !== undefined &&
    (!record(value.authoring) ||
      value.authoring.mode !== 'select' ||
      Object.keys(value.authoring).some(
        (key) => !['mode', 'selectedComponentId'].includes(key)
      ) ||
      (value.authoring.selectedComponentId !== undefined &&
        (typeof value.authoring.selectedComponentId !== 'string' ||
          !/^[a-zA-Z0-9._:-]{1,256}$/.test(value.authoring.selectedComponentId))))
  )
    return false;
  const scenario = value.scenario;
  if (
    scenario.requestedFlags !== undefined &&
    (!record(scenario.requestedFlags) ||
      Object.keys(scenario.requestedFlags).length > 200 ||
      Object.values(scenario.requestedFlags).some((flagValue) => typeof flagValue !== 'boolean'))
  )
    return false;

  if (
    Object.keys(scenario).some(
      (key) =>
        ![
          'locale',
          'scoreMethod',
          'serviceMedia',
          'technicianNotes',
          'technicianFirstName',
          'companyName',
          'locationName',
          'tippingEligible',
          'serviceLifecycle',
          'contactEligible',
          'reviewAvailability',
          'reviewDraw',
          'existingResponse',
          'requestedFlags',
        ].includes(key)
    )
  )
    return false;
  if (
    !['locale', 'technicianNotes', 'companyName', 'locationName'].every(
      (key) => typeof scenario[key] === 'string'
    ) ||
    (scenario.technicianFirstName !== undefined &&
      typeof scenario.technicianFirstName !== 'string') ||
    !scenario.locale ||
    !['nps', 'csat'].includes(scenario.scoreMethod as string) ||
    typeof scenario.tippingEligible !== 'boolean'
  )
    return false;
  if (
    !Array.isArray(scenario.serviceMedia) ||
    scenario.serviceMedia.length > 100 ||
    scenario.serviceMedia.some(
      (item) =>
        !record(item) ||
        !['image', 'video', 'voice'].includes(item.type as string) ||
        Object.keys(item).some(
          (key) => !['type', 'url', 'thumbnailUrl', 'label', 'notes'].includes(key)
        ) ||
        ['url', 'thumbnailUrl', 'label', 'notes'].some(
          (key) => item[key] !== undefined && typeof item[key] !== 'string'
        )
    )
  )
    return false;
  if (
    scenario.reviewAvailability !== undefined &&
    !['sample', 'unavailable', 'matched', 'matched_no_alternatives'].includes(
      scenario.reviewAvailability as string
    )
  )
    return false;
  if (
    scenario.reviewDraw !== undefined &&
    (typeof scenario.reviewDraw !== 'number' ||
      !Number.isFinite(scenario.reviewDraw) ||
      scenario.reviewDraw < 0 ||
      scenario.reviewDraw >= 1)
  )
    return false;
  if (
    scenario.serviceLifecycle !== undefined &&
    (typeof scenario.serviceLifecycle !== 'string' ||
      !['initial_service', 'regular_service'].includes(scenario.serviceLifecycle))
  )
    return false;
  if (scenario.contactEligible !== undefined && typeof scenario.contactEligible !== 'boolean')
    return false;
  const prior = scenario.existingResponse;
  if (
    prior !== undefined &&
    prior !== null &&
    (!record(prior) ||
      Object.keys(prior).some(
        (key) =>
          !['comments', 'rating', 'ratingId', 'scoreMethod', 'submitted', 'tags'].includes(key)
      ) ||
      !Number.isFinite(prior.rating) ||
      !Number.isSafeInteger(prior.ratingId) ||
      prior.submitted !== true ||
      !['NPS', 'CSAT'].includes(prior.scoreMethod as string) ||
      (prior.comments !== undefined &&
        prior.comments !== null &&
        typeof prior.comments !== 'string') ||
      (prior.tags !== undefined &&
        prior.tags !== null &&
        (!Array.isArray(prior.tags) || prior.tags.some((tag) => typeof tag !== 'string'))))
  )
    return false;
  if (value.initialScreenId !== undefined && typeof value.initialScreenId !== 'string')
    return false;
  return value.initialValues === undefined || record(value.initialValues);
}

export function validPreviewConfiguration(value: unknown): value is Record<string, unknown> {
  if (!record(value) || !record(value.flow)) return false;
  const flow = value.flow;
  if (
    typeof flow.id !== 'string' ||
    !flow.id ||
    !Array.isArray(flow.screens) ||
    flow.screens.length === 0 ||
    flow.screens.length > 200 ||
    !record(flow.brand)
  )
    return false;
  const ids = new Set<string>();
  let components = 0;
  for (const screen of flow.screens) {
    if (
      !record(screen) ||
      typeof screen.id !== 'string' ||
      !screen.id ||
      ids.has(screen.id) ||
      !Array.isArray(screen.components)
    )
      return false;
    ids.add(screen.id);
    for (const component of screen.components) {
      if (
        ++components > 2_000 ||
        !record(component) ||
        typeof component.id !== 'string' ||
        !component.id ||
        typeof component.type !== 'string' ||
        !component.type
      )
        return false;
    }
  }
  if (
    value.initialScreenId !== undefined &&
    (typeof value.initialScreenId !== 'string' || !ids.has(value.initialScreenId))
  )
    return false;
  return value.initialValues === undefined || record(value.initialValues);
}

export function message(
  type: PreviewMessage['type'],
  session: string,
  revision: number,
  payload?: Record<string, unknown>,
  instance = session,
  version: PreviewMessage['version'] = PREVIEW_PROTOCOL
): PreviewMessage {
  return {
    channel: PREVIEW_CHANNEL,
    version,
    session,
    instance,
    revision,
    type,
    ...(payload ? { payload } : {}),
  };
}
